import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import ProgramCard from '../components/ProgramCard';
import Navbar from '../components/Navbar';
import BackButton from '../components/common/BackButton';
import { getPublicProgrammes } from '../services/siteContentService';

const DEFAULT_PROGRAMMES = [
  { slug: 'adahi', title: 'أضحية العيد', description: 'الرعاية الفورية للحالات الطبية الطارئة، مخففةً أعباء المرضى وذويهم.', icon: 'adahi' },
  { slug: 'namaa', title: 'نـماء', description: 'تنفيذ مشاريع دخل لتحسين مستوى المعيشة للأسر الفقيرة.', icon: 'namaa' },
  {
    slug: 'kafala',
    title: 'كفالة',
    description:
      '"أيتامنا" هو مشروع إنساني يهدف إلى توفير رعاية شاملة للأيتام، من خلال تقديم الدعم الاجتماعي والتربوي والتعليمي والصحي، بما يسهم في تحسين جودة حياتهم، وتنمية قدراتهم، وتمكينهم من بناء مستقبل أكثر استقرارًا وإشراقًا.',
    icon: 'kafala',
  },
  {
    slug: 'saqya',
    title: 'سقيا',
    description:
      'يأتي مشروع "سقيا عطاء" استجابةً لحاجة الساكنة إلى المياه النقية، حيث يعمل على توفير مصادر مستدامة لمياه الشرب من خلال حفر وتجهيز الآبار وتسهيل وصول المياه إلى الأسر بالمناطق والدواوير التي تعاني من ندرتها.',
    icon: 'saqya',
  },
  { slug: 'imaar', title: 'إعمار', description: 'ترميم وصيانة للمؤسسات الدينية والتعليمية وتوفير سكن للأسر الفقيرة.', icon: 'imaar' },
  { slug: 'ramadan', title: 'مائدة الرحمن', description: 'برنامج مائدة الرحمن لإفطار الصائمين ودعم الأسر المحتاجة خلال شهر رمضان المبارك.', icon: 'ramadan' },
  {
    slug: 'omra',
    title: 'ضيوف الرحمن',
    description:
      'يتيح برنامج خدمة ضيوف الرحمن للقادمين إلى الحرمين الشريفين من الخارج والداخل، مرافق ذات جودة عالية، تساعد الجميع على أن ينعموا بتجربة إيمانية مميزة لا تنسى.',
    icon: 'omra',
  },
  {
    slug: 'caravane',
    title: 'قوافل تضامنية',
    description:
      'القافلة التضامنية مبادرة إنسانية تسعى إلى مدّ يد العون للأسر المحتاجة خلال الأزمات والظروف الصعبة، من خلال تنظيم حملات موسمية تهدف إلى توفير الاحتياجات الأساسية وتحسين ظروف العيش للفئات الأكثر هشاشة.',
    icon: 'ighatha',
  },
];

const ProgramsPage = () => {
  const { t } = useTranslation();
  const [programs, setPrograms] = useState(() => {
    try {
      const raw = localStorage.getItem('khayr_public_programmes_list_v2');
      const parsed = raw ? JSON.parse(raw) : null;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_PROGRAMMES;
    } catch {
      return DEFAULT_PROGRAMMES;
    }
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await getPublicProgrammes();
        if (data && data.length > 0) {
          const mapped = data.map((p) => ({
            slug: p.slug === 'ihyaa' ? 'adahi' : p.slug === 'imaar' ? 'ramadan' : p.slug,
            title: p.title,
            description: p.description,
            icon: p.icon || p.slug,
          }));
          setPrograms(mapped);
          try {
            localStorage.setItem('khayr_public_programmes_list_v2', JSON.stringify(mapped));
          } catch {
            // ignore quota errors
          }
        }
      } catch (error) {
        console.error('Erreur chargement programmes:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <Helmet>
        <title>{`${t('public.programsPage.metaTitle')} | ${t('public.brand.titleSuffix')}`}</title>
      </Helmet>
      <Navbar />
      <div className="py-12 px-4 sm:px-6 lg:px-8">
      <BackButton />
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('public.programsPage.title')}</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('public.programsPage.lead')}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-green-500 mx-auto mt-6"></div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {programs.map((program) => (
              <ProgramCard key={program.slug} program={program} />
            ))}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default ProgramsPage;
